import Typography from '@mui/material/Typography';
import WindroseTable from './WindroseTable';
import WindroseGraph from './WindroseGraph';
import { getAcisData, setupFilters, passesFilter } from '../js/dataAccessRoutines';
import { formatDateRange, formatHourFilter, formatDayFilter } from "../js/utilityRoutines"

// Wind Rose Processing //

const setWindResults = (windResults, setResult) => {
  if (windResults.highChartOptions.series) {
      const code = (
          <>
            {windResults.userParams.output.graph &&
              <WindroseGraph 
                  options={windResults.highChartOptions}
                  userParams={windResults.userParams}
              />
            }
            {windResults.userParams.output.table &&
              <WindroseTable 
                  hcdata={windResults.highChartOptions}
                  userParams={windResults.userParams}
                  windtableData={windResults.windtableData}
              />
            }
          </>
      );
      setResult(code)
  } else {
    setResult("")
  }
}

const loadWindRoseData = (windroseData, windtableData, stnName, userParams) => {
  const dayFilter = formatDayFilter(userParams);
  const hourFilter = formatHourFilter(userParams).replace(".","")
  const subtitleText = userParams.pctcnt.charAt(0).toUpperCase() + userParams.pctcnt.slice(1) + 
        " of winds blowing from the indicated direction" +
        "<br />" + formatDateRange(userParams).replace(".","") +
        "<br />" + dayFilter + (dayFilter && hourFilter ? "; " : "") + hourFilter
 return {
    highChartOptions: {
      series: windroseData, 
      title: {text: stnName}, 
      subtitle: {text: subtitleText},
    }, 
    windtableData: {
      ...windtableData, 
      dayFilter: dayFilter, 
      hourFilter: hourFilter
    },
    userParams: userParams
  }
}

const hcSeries = (databins, speedSums, speedCnts, sumObs, sumMiss, userParams, speedbins, stnName) => {
    // format binned data for HighCharts (hcData) and the table (tableVals)
    const compass = ['---', 'NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW','N','Variable']
    const emspace = String.fromCharCode(8195)
    const gtsymbol = String.fromCharCode(62)
    const lesymbol = String.fromCharCode(8804)
    const wdirint = 360.0 / userParams.directbincnt
    let hcData = []
    let tableVals = {}
    let avgSpeeds = {}
    const tabledata = []
    const sb = speedbins
    const calmcnt = databins[0][0]
    const sumCalm = userParams.pctcnt === 'percent' ? ((calmcnt / sumObs) * 100) : calmcnt
    const formatValue = (value, last) => {
      const res = userParams.pctcnt === 'percent' || last ? 1 : 0
      return ((Math.round(value * 10) / 10.0).toFixed(res)).replace("-999.0","-")
    }
    databins.shift()  //don't pass calm winds to wind rose
    for (let i = 1; i <= userParams.directbincnt + 1; i += 1) {
      let wkey = null
      let wd = userParams.directbincnt === 16 ? compass[i] : i * wdirint
      if (wd === 360) {
        wkey = "0"
      } else if (i === userParams.directbincnt + 1) {
        wkey = "Variable"
      } else {
        wkey = wd.toString()
      }
      tableVals[wkey] = Array(speedbins.length - 1)
      avgSpeeds[wkey] = speedCnts[i] > 0 ? speedSums[i]/speedCnts[i] : -999
    }
    databins.forEach((ws, i) => {
      let spdinf = {}
      if (sb[i+1] === 9999) {
        spdinf.name = emspace + gtsymbol + sb[i] + emspace
      } else if (sb[i] === 0) {
        spdinf.name = emspace + lesymbol + sb[i+1] + emspace
      } else {
        spdinf.name = sb[i] + " to " + sb[i+1]
      }
      spdinf._colorIndex = i
      spdinf.data = []
      ws.forEach((cnt, j) => {
        let wkey = null
        if (j === 0) {
          if (cnt !== 0) {
            console.log('Unexpected non-zero direction for calm wind speed')
          }
        } else {
          let wd = userParams.directbincnt === 16 ? compass[j] : j * wdirint
          const ws = userParams.pctcnt === 'percent' ? (cnt/sumObs)*100 : cnt
          const roundws = userParams.pctcnt === 'percent' ? Math.round(ws*10)/10 : ws
          if (wd === 360) {
            wkey = "0"
            spdinf.data.unshift([wkey,roundws])
          } else if (wd === 'N') {
            wkey = "N"
            spdinf.data.unshift([wkey,roundws])
          } else if (j === userParams.directbincnt + 1) {
            wkey = "Variable"
          } else {
            wkey = wd.toString()
            spdinf.data.push([wkey,roundws])
          }
          tableVals[wkey][i] = ws
        }
      })
      hcData.push(spdinf)
    })
    Object.keys(tableVals).forEach((wdrow) => {
      const rowSum = tableVals[wdrow].reduce((a,c) => a + c, 0)
      tableVals[wdrow].push(rowSum)
      tableVals[wdrow].push(avgSpeeds[wdrow])
    })

    // Table construction
    Object.keys(tableVals).map((wkey, wd) => {
      const rowdata = {};
      rowdata.index = wd;
      rowdata.wdir = wkey;
      tableVals[wkey].map((wscnt,j) => {
        if (j === tableVals[wkey].length - 1) {
          rowdata.avg = formatValue(wscnt, true)
        } else if (j === tableVals[wkey].length - 2) {
          rowdata.all = formatValue(wscnt, false)
        } else {
          rowdata[j] = formatValue(wscnt, j === tableVals[wkey].length-1)
        }
      })
      tabledata.push(rowdata)
    })
    // Add Calm row
    const rowdata = {}
    rowdata.index = Object.keys(tableVals).length
    rowdata.wdir = "Calm"
    for (let i = 0; i < tableVals['Variable'].length - 2; i+=1) {
      rowdata[i] = "-"
    }
    rowdata.all = formatValue(sumCalm, false)
    rowdata.avg = "-"
    tabledata.push(rowdata)

    const windtableData = {
      tabledata: tabledata,
      sumObs: sumObs.toLocaleString("en"),
      sumMiss: sumMiss.toLocaleString("en"),
    }
    const windResults = loadWindRoseData(hcData, windtableData, stnName, userParams)
    return windResults
}

const getBin = (ws, wd, userParams, speedbins) => {
    var wspd, wdir;
    const wdirint = 360.0 / userParams.directbincnt;
    const wdirinc = 0.5 * wdirint;
    let wspdbin = "M";
    let wdirbin = "M";
    //determine speed bin
    if (ws === "M") {
      wspdbin = "M"
    } else {
      wspd = parseFloat(ws)
      if (wspd <= speedbins[0] || wd === "CLM") {   // Calm winds determined by speed or direction
        wspdbin = 0
      } else {
        for (let i = 0; i < speedbins.length-1; i += 1) {
          if (wspd > speedbins[i] && wspd <= speedbins[i+1]) {
            wspdbin = i + 1
            break
          }
        }
      }
    }
    // determine direction bin
    if (wd === "M") {
      wdirbin = "M"
    } else if (wd === "CLM" || wspdbin === 0) {   // Calm winds determined by speed or direction
      wdirbin = 0
    } else if (wd === "VRB") {                    // Variable wind direction 
      wdirbin = userParams.directbincnt + 1
    } else {
      wdir = parseFloat(wd)
      if (wdir >= 360 - wdirinc || wdir < 0 + wdirinc) {
        wdirbin = userParams.directbincnt
      } else {
        for (let i = 1; i < userParams.directbincnt; i += 1) {
          const b = i * wdirint
          if (wdir >= b - wdirinc && wdir < b + wdirinc) {
            wdirbin = i
            break
          }
        }
      }
    }
    return {wspdbin, wdirbin}
}

const processWindData = (res, userParams, speedbins) => {
  const stnName = res.meta.name + ", " + res.meta.state
  const filters = setupFilters(userParams)
  let sumObs = 0
  let sumMiss = 0
  let databins = []
  let speedSums = Array(userParams.directbincnt+2).fill(0)
  let speedCnts = Array(userParams.directbincnt+2).fill(0)
  for (let k = 0; k < speedbins.length; k += 1) {
    databins.push(Array(userParams.directbincnt + 2).fill(0))  //extra bins for calm and variable
  }

  res.data.forEach((results, i) => {
    if (passesFilter(results[0], filters)) {
      const wspd = results[1]
      const wdir = results[2]
      let {wspdbin, wdirbin} = getBin(wspd, wdir, userParams, speedbins)
      if (wspdbin !== "M" && wdirbin !== "M") {
        speedSums[wdirbin] += parseFloat(wspd)  //Wind speed will always be a number here
        speedCnts[wdirbin] += 1
        databins[wspdbin][wdirbin] += 1
        sumObs += 1
      } else {
        sumMiss += 1
      }
    }
  })
  const windResults = hcSeries(databins, speedSums, speedCnts, sumObs, sumMiss, userParams, speedbins, stnName)
  return windResults
}

export function processWindrose(userParams, setResult, hourlyVx) {
    const speedbins = [...userParams.speedbins.filter(e => e !== ""), 9999]  // remove empty speed bins and add an ending 9999
    const sid = userParams.sid;
    const network = sid.indexOf(" ") > 0 ? sid.split(" ")[1] : 'icao';
    const sdate = [userParams.fromDate.year(),userParams.fromDate.month()+1,userParams.fromDate.date(),1] || [2024,1,1,1];
    // end hour 24, which is hour 0 the next day
    const nextday = new Date(userParams.toDate);
    nextday.setDate(userParams.toDate.date() + 1)
    const edate = [nextday.getFullYear(),nextday.getMonth()+1,nextday.getDate(),0] || [2025,1,1,0];
    const acisParams = {
        sid: sid,
        sDate: sdate,
        eDate: edate,
        elems: [
        {vX: hourlyVx[network].wspd, interval: [0,0,0,1], duration: 1, units: userParams.wsunits || 'miles/hr'},
        {vX: hourlyVx[network].wdir, interval: [0,0,0,1], duration: 1}
        ]
    }
    getAcisData(acisParams)
    .then(res => {
      const windResults = processWindData(res, userParams, speedbins);
      setWindResults(windResults, setResult)
    })
    .catch(error =>  { 
      console.log("Data retrieval error: " + error);  
      setResult((
        <Typography sx={{color: "red"}}>
          Error processing request - {error.acisStatus}
        </Typography>
      ))
    })
}